import { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  GoABlock,
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoASpacer,
} from '@abgov/react-components';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import { useNavigate } from 'react-router-dom';
import { fetchMediaListByCollisionId } from '../../state/slices/mediaSlice';
import useCsrService from '../../services/csr-api-redux';
import { isHitAndRun } from '../../state/slices/configurationSlice';
import { Collision } from '../../models/collision';
import { updateCollision } from '../../state/slices/collisionSlice';
import {
  pushNotification,
  removeAllNotifications,
  removeAllValidationErrorNotifications,
  removeNotification,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';

interface IntroductionComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const IntroductionComponent = ({
  navigationConfiguration,
}: IntroductionComponentProps) => {
  const navigate = useNavigate();
  const csrService = useCsrService();
  const dispatch = useAppDispatch();
  const stateNotification = useAppSelector(
    (state: RootState) => state.notifications,
  );
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const [skipSelected, setSkipSelected] = useState(false);

  const [initialCollisionType, setInitialCollisionType] = useState(
    localStorage.getItem('initialCollisionType') || '0',
  );

  const stateMedia = useAppSelector((state: RootState) => state.media);

  const [nextPage, setNextPage] = useState('');

  // Compare the initial collision type with the current one
  const [notificationSent, setNotificationSent] = useState<boolean | undefined>(
    undefined,
  );

  const collisionTypeChangeNotification = {
    body: "You've updated your vehicle damage information. Update your vehicle damage photos where applicable",
    notificationType: NotificationType.importantWarning,
  };

  useEffect(() => {
    const handleBackNavigation = (event: PopStateEvent) => {
      event.preventDefault();
      dispatch(removeAllNotifications());
    };

    window.addEventListener('popstate', handleBackNavigation);

    return () => {
      window.removeEventListener('popstate', handleBackNavigation);
    };
  }, []);

  useEffect(() => {
    if (stateConfiguration.uiTriggers.reviewDamageModified) {
      dispatch(removeAllNotifications());
      dispatch(
        pushNotification({
          body: "You've updated your vehicle damage information. Update your vehicle damage photos where applicable",
          notificationType: NotificationType.importantWarning,
        }),
      );
    }
  }, [stateConfiguration.uiTriggers.reviewDamageModified]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentNotification = stateNotification.notifications.find(
        (n) => n.body === collisionTypeChangeNotification.body,
      );

      if (currentNotification) {
        dispatch(removeNotification(currentNotification.id));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [stateNotification.notifications, dispatch]); // Ensure Redux state changes trigger re-evaluation

  useEffect(() => {
    //only set the initial collision type the first time we come to this component
    //then we can compare it to the currently selected collision type
    if (initialCollisionType == '0')
      localStorage.setItem(
        'initialCollisionType',
        stateCollision.collision.collisionType?.toString()!,
      );
  }, [initialCollisionType]);

  useEffect(() => {
    if (
      stateCollision.collision.collisionType !== Number(initialCollisionType)
    ) {
      //keep track of the currently selected colllision type
      localStorage.setItem(
        'initialCollisionType',
        stateCollision.collision.collisionType?.toString()!,
      );
      if (stateMedia.images.length > 0 && !notificationSent) {
        dispatch(pushNotification(collisionTypeChangeNotification));
        setNotificationSent(true);
      }
    } else {
      dispatch(removeAllValidationErrorNotifications());
      setNotificationSent(false);
    }
  }, [
    stateCollision.collision.collisionType,
    stateMedia.images,
    initialCollisionType,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(
      fetchMediaListByCollisionId({
        id: stateCollision.collision.id!,
        csrService,
      }),
    );
  }, []);

  useEffect(() => {
    let pageConfig = 'error';

    if (skipSelected) {
      if (
        isHitAndRun(stateCollision.collision.collisionType!, stateConfiguration)
      ) {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.hitandrun !== undefined,
        )!.hitandrun;
      } else {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.skip !== undefined,
        )!.skip;
      }
    } else {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.default !== undefined,
      )!.default;
    }

    setNextPage(pageConfig);
  }, [skipSelected]);

  useEffect(() => {
    if (skipSelected) {
      if (navigationConfiguration.updateMetadata) {
        let updatedCollision: Collision = { ...stateCollision.collision };

        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          false,
          false,
          true,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }

        dispatch(updateCollision(updatedCollision));
      }

      navigationConfiguration.navigate(nextPage);
    }
  }, [nextPage]);

  return (
    <div>
      <h1>Photo upload</h1>
      <GoASpacer vSpacing={'l'} />
      Upload photos of all damaged areas on your vehicle as a result of the
      collision.
      <GoASpacer vSpacing={'l'} />
      <GoACallout type="information" heading="Important" size="medium">
        Photos of the vehicle damage are a requirement of this online service.
        Failure to upload photos prevents you from submitting the report and
        obtaining a digital damage sticker
      </GoACallout>
      <GoASpacer vSpacing={'xl'} />
      Upload a maximum of 25 photos. They can be uploaded one at a time or as a
      batch (if applicable).
      <GoASpacer vSpacing={'xl'} />
      <h3>Upload requirements</h3>
      <ul className="goa-unordered-list">
        <li>Acceptable photo formats are: JPG, PNG, HEIC, HEIF, BMP or WebP</li>
        <li>Maximum file size is 10MB</li>
        <li>Video formats are currently not supported</li>
      </ul>
      <GoASpacer vSpacing={'xl'} />
      <GoAButtonGroup alignment="start">
        <GoAButton
          type="primary"
          onClick={() => {
            navigationConfiguration.navigate(nextPage);
          }}
        >
          Continue
        </GoAButton>
        <GoAButton
          type="secondary"
          onClick={() => {
            setSkipSelected(true);
          }}
        >
          Skip for now
        </GoAButton>
        <GoAButton
          type="tertiary"
          onClick={() => {
            if (navigationConfiguration.goToReview)
              navigationConfiguration.goToReview();
          }}
        >
          View report summary
        </GoAButton>
      </GoAButtonGroup>
    </div>
  );
};

export default IntroductionComponent;
